<template>
  <div>
    <vx-card class="org-card">
      <div>
        <h3 class="mb-base">
          <span class="text-primary">{{ orgData.name }}</span> Admins
        </h3>
        <div class="my-base-large">
          <h5 class="mb-3">Add Admin</h5>
          <div class="mb-3">
            <vs-input v-validate="'required|email|min:3'" data-vv-validate-on="blur" :disabled="!isUserLoggedIn"
              name="Email" placeholder="Email Address" icon-no-border icon="icon icon-mail" icon-pack="feather"
              v-model="email" class="w-full lg:w-1/2" @keyup="adminExists()" />
              
            <!-- NEW: Display dynamic error message from backend -->
            <span v-if="adminExistErrorMsg" class="text-danger text-sm">{{ adminExistErrorMsg }}</span>
            <span v-else-if="email && !validEmail" class="text-danger text-sm">{{ errors.first("Email") }}</span>
          </div>
          <vs-button class="invite-btn" :disabled="adminAlreadyExists || !validEmail" @click.stop="inviteAdmin()">Send
            Invite</vs-button>
        </div>
        <div v-if="orgData && orgData.admin && orgData.subAdmins">
          <h5 class="mb-3">Admins</h5>
          <!-- UPDATED: Now uses displayAdmins instead of subAdmins -->
          <vs-table max-items="7" pagination :data="displayAdmins">
            <template slot="thead">
              <vs-th> Email </vs-th>
              <vs-th> Name </vs-th>
              <vs-th> Role </vs-th>
              <vs-th> Status </vs-th>
              <vs-th> Revoke Access </vs-th>
            </template>

            <template>
              <!-- SUPER ADMIN ROW -->
              <vs-tr v-if="orgData && orgData.admin" key="super-admin-data">
                <vs-td :data="orgData.admin.email">
                  <vs-row vs-align="center">
                    <div class="float-leftt mr-3">
                      {{ orgData.admin.email }}
                    </div>
                  </vs-row>
                </vs-td>

                <vs-td v-if="orgData.admin.firstName" :data="orgData.admin.firstName">
                  {{ orgData.admin.firstName }} {{ orgData.admin.lastName }}
                </vs-td>
                <vs-td v-else></vs-td>

                <vs-td data="Super Admin">
                  <small>Super Admin</small>
                </vs-td>

                <vs-td data="Active">
                  <vs-chip color="success">Active</vs-chip>
                </vs-td>

                <vs-td data="Super admin cannot be removed">
                  <small>Super admin cannot be removed</small>
                </vs-td>
              </vs-tr>

              <!-- SUB ADMINS ROW (UPDATED) -->
              <vs-tr v-for="(tr, indextr) in displayAdmins" :key="indextr">
                <vs-td :data="tr.email">
                  {{ tr.email }}
                </vs-td>

                <!-- FIXED: Shows "Pending Registration" if the user has no name data yet -->
                <vs-td :data="tr.userData && tr.userData[0] ? tr.userData[0].firstName : 'Pending'">
                  <span v-if="tr.userData && tr.userData[0] && tr.userData[0].firstName">
                    {{ tr.userData[0].firstName }} {{ tr.userData[0].lastName }}
                  </span>
                  <span v-else class="text-grey italic" style="color: #999;">
                    Pending Registration...
                  </span>
                </vs-td>

                <vs-td data="Sub-Admin">
                  <small>Sub-Admin</small>
                </vs-td>

                <!-- FIXED: Beautifully shows Pending vs Active Status -->
                <vs-td :data="tr.status">
                  <vs-chip v-if="tr.status === 'active'" color="success">Active</vs-chip>
                  <vs-chip v-else color="warning">Pending</vs-chip>
                </vs-td>

                <vs-td>
                  <vs-button color="danger" size="small" @click.stop="revokeSubAdmin(tr._id || tr.email)">Revoke</vs-button>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import axios from 'axios';
const PYTHON_API_URL = process.env.VUE_APP_API_URL;

export default {
  data() {
    return {
      email: null,
      adminExistErrorMsg: "",
      typingTimer: null,
      localPendingInvites: [] // NEW: Stores immediate invites before refresh
    };
  },
  computed: {
    isUserLoggedIn() { return this.$store.state.auth.isUserLoggedIn; },
    organizationId() { return this.$store.state.organization.orgData._id; },
    userData() { return this.$store.state.users.data; },
    orgData() { return this.$store.state.organization.orgData; },
    subAdmins() {
      if (this.orgData) return this.orgData.subAdmins || [];
      return [];
    },
    // NEW: Merges backend admins with local immediate invites
    displayAdmins() {
      let backendAdmins = this.subAdmins;
      let backendEmails = backendAdmins.map(a => a.email.toLowerCase());
      
      // Only show local invites if the backend hasn't returned them yet
      let visibleLocals = this.localPendingInvites.filter(a => !backendEmails.includes(a.email.toLowerCase()));
      
      return [...backendAdmins, ...visibleLocals];
    },
    validEmail() {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(this.email).toLowerCase());
    },
    isCurrentUserSubAdmin() {
      if (this.userData && this.orgData && this.orgData.admin) {
        return this.userData._id !== this.orgData.admin._id;
      }
      return true;
    },
    adminAlreadyExists() {
      return this.adminExistErrorMsg !== "";
    },
  },
  methods: {
    adminExists() {
      if (!this.email) {
        this.adminExistErrorMsg = "";
        return;
      }
      const inputEmail = this.email.toLowerCase().trim();
      clearTimeout(this.typingTimer);

      this.typingTimer = setTimeout(async () => {
        // 1. Local Check: Is it the Super Admin?
        if (this.orgData && this.orgData.admin && this.orgData.admin.email.toLowerCase() === inputEmail) {
          this.adminExistErrorMsg = "This user is the Super Admin.";
          return;
        }

        // 2. Local Check: Are they already an ACTIVE admin in the table?
        const existingAdmin = this.displayAdmins.find(e => e.email.toLowerCase() === inputEmail);
        if (existingAdmin && existingAdmin.status === 'active') {
          this.adminExistErrorMsg = "This user is already an active admin.";
          return;
        }

        try {
          const response = await axios.get(`${PYTHON_API_URL}/api/organization/check-admin-alias`, {
            params: { email: inputEmail, org_id: this.organizationId }
          });
          
          if (response.data.is_admin) {
            // BACKEND OVERRIDE: 
            // The Python backend is returning TRUE for revoked users.
            // Since they passed our active checks above, we will clear the error
            // to unlock the "Send Invite" button so you can re-invite them.
            this.adminExistErrorMsg = ""; 
          } else {
            this.adminExistErrorMsg = "";
          }
        } catch (error) {
          console.error("Failed to check alias", error);
        }
      }, 500); 
    },
    inviteAdmin() {
      const inputEmail = this.email.toLowerCase().trim();

      // 1. Prevent inviting someone who is already active
      const existingAdmin = this.displayAdmins.find(e => e.email.toLowerCase() === inputEmail);
      if (existingAdmin && existingAdmin.status === 'active') {
        this.adminExistErrorMsg = "This user is already an active admin.";
        return;
      }

      this.$vs.loading();

      // 2. PERFECT MATCH PAYLOAD FOR YOUR VUEX ACTION
      const payload = {
        email: inputEmail,
        organizationId: this.organizationId,
        organizationName: this.orgData.name,
        status: "pending",
        maxRole: "sub-admin"
      };

      // 3. EXACT VUEX ACTION NAME FOUND IN YOUR FILE
      this.$store.dispatch("organization/inviteOrganizationAdmin", payload)
        .then(() => {
          this.$vs.loading.close();
          this.$vs.notify({
            title: "Success",
            text: "Invitation sent to the new admin.",
            color: "success",
            icon: 'check_circle'
          });
          
          // Instantly show in UI as Pending
          if(this.localPendingInvites) {
             this.localPendingInvites.push({
               email: inputEmail,
               status: 'pending',
               userData: [] // Shows "Pending Registration..." text
             });
          }

          this.$store.dispatch("organization/fetchOrganization", this.organizationId);
          this.email = null; 
        })
        .catch((err) => {
          this.$vs.loading.close();
          this.$vs.notify({
            title: "Error",
            text: "Invitation could not be sent.",
            color: "danger",
            icon: 'error'
          });
          console.error("Invite admin error:", err);
        });
    },
    revokeSubAdmin(orgSubAdminId) {
      // Allow revocation using email if it's a local pending invite missing an ID
      const targetId = typeof orgSubAdminId === 'string' && orgSubAdminId.includes('@') ? null : orgSubAdminId;
      
      if(!targetId) {
         // Local UI cleanup if they revoke before refreshing
         this.localPendingInvites = this.localPendingInvites.filter(i => i.email !== orgSubAdminId);
         this.$vs.notify({ title: "Invite Canceled", color: "primary" });
         return;
      }

      this.$store
        .dispatch("organization/revokeSubAdmin", { orgSubAdminId: targetId })
        .then(() => {
          this.$vs.loading.close();
          this.$vs.notify({
            title: "Sub-Admin access revoked.",
            color: "primary",
          });
        })
        .catch((err) => {
          this.$vs.loading.close();
          this.$vs.notify({
            title: "Cannot revoke Sub-Admin access",
            color: "danger",
          });
        });
    },
  },
};
</script>

<style scoped>
.my-base-large {
  margin: 3rem auto 4rem auto;
}

.invite-btn {
  border-radius: 4px;
}

.vs-con-table>>>.vs-table--not-data {
  display: none !important;
}
</style>
